import whitelist from './whitelist.json'
import pick from 'lodash/pick'
import { logger } from 'services/logger'

const { events: eventsWhitelist, userProperties: intercomUserProperties } =
  whitelist.intercom
const intercomEvents = new Set(eventsWhitelist)

const INTERCOM_READY_TIMEOUT_MS = 120000
const INTERCOM_READY_CHECK_INTERVAL_MS = 250

class IntercomService {
  didInit = false

  hasSettled = false

  didLoadSuccessfully = false

  readyPromise = new Promise((resolve) => {
    this.readyResolve = resolve
  })

  hasPendingShow = false

  scrollPosition = 0

  init() {
    if (this.didInit) return

    this.initIntercom()
  }

  initIntercom() {
    if (this.didInit) return
    this.didInit = true

    try {
      // `self` is needed for onerror callback inside the IIFE where `this` context differs
      const self = this

      ;(function A() {
        let i = function B() {
          i.c(arguments)
        }
        let w = window
        let ic = w.Intercom
        if (typeof ic === 'function') {
          ic('reattach_activator')
          ic('update', w.intercomSettings)
        } else {
          let d = document
          i.q = []
          i.c = function C(args) {
            i.q.push(args)
          }
          w.Intercom = i
          let retryCount = 0
          let l = function D() {
            let s = d.createElement('script')
            s.type = 'text/javascript'
            s.async = true
            s.src = `https://widget.intercom.io/widget/${window.KEYS.intercomAppId}`
            s.onerror = () => {
              try {
                s.remove()
                if (retryCount < 3) {
                  const delay = 2000 * 2 ** retryCount
                  retryCount++
                  setTimeout(l, delay)
                } else {
                  logger.warning('Failed to load Intercom script after retries')
                  self.setReady(false)
                }
              } catch (_e) {
                self.setReady(false)
              }
            }
            document.head.appendChild(s)
          }
          if (document.readyState === 'complete') {
            l()
          } else if (w.attachEvent) {
            w.attachEvent('onload', l)
          } else {
            w.addEventListener('load', l, false)
          }
        }
      })()

      // Boot must be called before waitForIntercomReady to ensure boot command is queued first
      window.Intercom('boot', {
        app_id: window.KEYS.intercomAppId,
        hide_default_launcher: true, // Don't show the intercom button
      })

      // Start polling for ready state after boot command is issued
      this.waitForIntercomReady()

      // Register event handlers to preserve scroll position
      window.Intercom('onShow', () => {
        this.scrollPosition =
          window.pageYOffset || document.documentElement.scrollTop
      })

      window.Intercom('onHide', () => {
        // Restore scroll position when Intercom is closed
        setTimeout(() => {
          window.scrollTo(0, this.scrollPosition)
        }, 0)
      })
    } catch (error) {
      logger.error('Error initiating Intercom: ', error)
      this.setReady(false)
    }
  }

  waitForIntercomReady() {
    if (this.hasSettled) return

    const startTime = Date.now()

    const checkReady = () => {
      // Check if Intercom is fully booted
      const isIntercomBooted =
        typeof window.Intercom === 'function' && window.Intercom.booted === true

      if (isIntercomBooted) {
        this.setReady(true)
        return
      }

      if (Date.now() - startTime > INTERCOM_READY_TIMEOUT_MS) {
        logger.warning('Intercom ready timeout - widget may not have loaded')
        this.setReady(false)
        return
      }

      setTimeout(checkReady, INTERCOM_READY_CHECK_INTERVAL_MS)
    }

    checkReady()
  }

  setReady(success) {
    if (this.hasSettled) return

    this.hasSettled = true
    this.didLoadSuccessfully = success

    if (this.readyResolve) {
      this.readyResolve(success)
    }

    if (success && this.hasPendingShow) {
      this.hasPendingShow = false
      this.executeShowCommand('home')
    } else {
      this.hasPendingShow = false
    }
  }

  getReadyPromise() {
    return this.readyPromise
  }

  executeShowCommand(space) {
    if (!this.didLoadSuccessfully) {
      logger.warning(
        'Cannot show Intercom - widget did not load successfully',
        {
          space,
        }
      )
      return
    }

    try {
      window.Intercom('showSpace', space)
    } catch (error) {
      logger.error('Error showing Intercom', { space, error })
    }
  }

  invokeIntercom(...args) {
    if (!this.didInit) {
      this.initIntercom()
    }

    window.Intercom(...args)
  }

  track(eventName, eventProperties) {
    if (intercomEvents.has(eventName)) {
      this.invokeIntercom('trackEvent', eventName, eventProperties)
    }
  }

  setUserProperties(userProperties) {
    const props = pick(userProperties, intercomUserProperties)

    if (Object.keys(props).length > 0) {
      this.invokeIntercom('update', props)
    }
  }

  setActiveExperiments(userProperties) {
    const props = {}
    for (const [key, value] of Object.entries(userProperties)) {
      if (key.startsWith('[Feature] ')) {
        props[key.replace('[Feature] ', 'Feature - ')] = value
      }
    }

    if (Object.keys(props).length > 0) {
      this.invokeIntercom('update', props)
    }
  }

  setOnShowEvent(cb) {
    this.invokeIntercom('onShow', cb)
  }

  identifyUser(userId) {
    this.setUserProperties({ user_id: userId })
  }

  setName(name) {
    this.setUserProperties({ name })
  }

  setEmail(email) {
    this.setUserProperties({ email })
  }

  show() {
    // Save scroll position before showing Intercom
    this.scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop
    this.showSpace('home')
  }

  showSpace(space) {
    // Save scroll position before showing Intercom
    this.scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop

    if (!this.didInit) {
      logger.info('showSpace called before Intercom initialized', { space })
      this.initIntercom()
    }

    if (this.hasSettled && this.didLoadSuccessfully) {
      this.executeShowCommand(space)
    } else if (!this.hasSettled) {
      this.hasPendingShow = true
    }
  }
}

export const intercomService = new IntercomService()
